import { TRANSFER_MAX_CHARS, formatTransferCode, isTransferKey, normalizeTransferCode, pickTransferData } from '@brainfart/shared';
import { API_BASE } from './apiBase';
import { duelPlayerId } from './duelInbox';
import { getStoredDisplayName } from './displayName';
import { getProfile } from './profileStore';

// Moves progress from Safari into the iOS home-screen app, which starts with
// empty storage. Safari uploads a snapshot for a short one-time code; the app
// claims it. See shared/src/transfer.ts and server/src/transfer.ts.

const DECLINED_KEY = 'bf_restore_declined';

// Something worth carrying over: a name, or any progress.
export function hasProgress(): boolean {
  if (getStoredDisplayName().trim()) return true;
  const p = getProfile();
  return Object.keys(p.categoryXP).length > 0 || p.streak.count > 0 || p.stats.dailyGames + p.stats.soloGames > 0;
}

function snapshot(): Record<string, string> {
  const entries: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && isTransferKey(key)) entries[key] = localStorage.getItem(key) ?? '';
    }
  } catch { /* storage blocked: nothing to carry */ }
  duelPlayerId(); // make sure there is a player id to carry
  const id = localStorage.getItem('bf_player_id');
  if (id) entries.bf_player_id = id;
  return pickTransferData(entries);
}

// Uploads the snapshot and returns the code to show ("K7QM-2XPD"), or null if
// it couldn't be made (offline, nothing to carry). Never throws.
export async function createRestoreCode(): Promise<string | null> {
  try {
    const data = snapshot();
    if (JSON.stringify(data).length > TRANSFER_MAX_CHARS) delete data.bf_ratings; // only a cache, rebuilt from the server
    const res = await fetch(`${API_BASE}/api/profile/transfer`, {
      method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ playerId: duelPlayerId(), data }),
    });
    if (!res.ok) return null;
    return formatTransferCode((await res.json() as { code: string }).code);
  } catch {
    return null;
  }
}

export type RestoreResult = 'ok' | 'invalid' | 'error';

// Claims a code and writes the carried entries into this device's storage.
export async function claimRestoreCode(input: string): Promise<RestoreResult> {
  const code = normalizeTransferCode(input);
  if (!code) return 'invalid';
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/profile/claim`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ code }) });
  } catch {
    return 'error';
  }
  if (res.status === 404) return 'invalid';
  if (!res.ok) return 'error';
  try {
    const { data } = await res.json() as { data: Record<string, unknown> };
    const carried = pickTransferData(data); // never trust the server's word for which keys are allowed
    for (const [key, value] of Object.entries(carried)) localStorage.setItem(key, value);
    return Object.keys(carried).length > 0 ? 'ok' : 'invalid';
  } catch {
    return 'error';
  }
}

export function restoreDeclined(): boolean {
  try { return localStorage.getItem(DECLINED_KEY) !== null; } catch { return true; }
}

export function declineRestore() {
  try { localStorage.setItem(DECLINED_KEY, String(Date.now())); } catch { /* ignore */ }
}
